import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { FaArrowLeftLong } from "react-icons/fa6";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import Swal from "sweetalert2";

import Button from "../../../components/common/Button/Button";
import ReusableMuiTable from "../../../components/common/Table/Table";
import FormInput from "../../../components/common/FormInput/FormInput";
import { showToast } from "../../../components/common/ToastNotification/ToastNotification";


// import {getProductTypeBenefitById,getProductTypeBenifits,addProductTypeBenifit,updateProductTypeBenifit,deleteProductTypeBenifits} from "../../../api/service/products.service";
import { getProductBenifit, deleteProductTypeBenifits, updateProductTypeBenifit, addProductTypeBenifit, getProductTypeBenifitById } from "../../../api/service/products.service";

const ProductTypeBenefits = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [benefits, setBenefits] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [formValue, setFormValue] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [searchbenifits, setSearchBenifits] = useState("");


  const fetchBenefits = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      const res = await getProductBenifit(id, page, 10, searchbenifits);

      if (res?.success) {
        setBenefits(res.data.data || []);
        setTotalCount(res.data.total || 0);
      }
    } catch {
      showToast.error("Failed to load product benefits");
    } finally {
      setLoading(false);
    }
  }, [id, page, searchbenifits]);


  useEffect(() => {
    fetchBenefits();
  }, [fetchBenefits]);


  const handleEdit = async (row) => {
    try {
      setLoading(true);
      const res = await getProductTypeBenifitById(row.id);
      // console.log(res,"benefit details....");
      if (res?.success) {
        setFormValue(res.data.text);
        setEditingId(row.id);
      }
    } catch {
      showToast.error("Failed to load benefit details");
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This product benefit will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const res = await deleteProductTypeBenifits(row.id);

      if (res?.success) {
        showToast.success("Product benefit deleted");
        fetchBenefits();
      }
    } catch {
      Swal.fire("Error", "Delete failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async () => {
    const value = formValue.trim();
    // console.log(id,"product_id....")
    if (!value) {
      setError("Product benefit cannot be empty");
      return;
    }

    try {
      setLoading(true);

      let res;
      if (editingId) {
        res = await updateProductTypeBenifit({
          product_benefit_id: editingId,
          text: value,

        });
      } else {
        res = await addProductTypeBenifit({
          product_id: id,
          text: value,
        });
      }

      showToast.success(res?.message || "Product benefit saved");

      setFormValue("");
      setEditingId(null);
      setError("");

      fetchBenefits();
    } catch {
      showToast.error("Action failed");
    } finally {
      setLoading(false);
    }
  };


  const columns = [
    {
      name: "text",
      label: "Product Benefit",
    },
    {
      name: "actions",
      label: "Action",
      options: {
        sort: false,
        setCellProps: () => ({
          style: { textAlign: "right", width: 160 },
        }),
        customBodyRenderLite: (index) => {
          const row = benefits[index];
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => handleEdit(row)}
                  sx={{ color: "#C39B22", p: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  onClick={() => handleDelete(row)}
                  sx={{ color: "#d32f2f", p: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  useEffect(() => {
    setPage(1);
  }, [searchbenifits]);

  return (
    <div className="py-3">

      <div
        className="d-flex align-items-center gap-2 mb-3"
        style={{ cursor: "pointer" }}
        onClick={() => navigate(-1)}
      >
        <FaArrowLeftLong />
        <h3 className="mb-0 management-tile ml-2">
          Add / Edit Product  Benefits
        </h3>
      </div>


      <Row className="mb-3">
        <Col xs={12}>
          <FormInput
            value={formValue}
            onChange={(e) => {
              setFormValue(e.target.value);
              setError("");
            }}
            placeholder="Enter product benefit..."
            disabled={loading}
          />
          {error && <small className="text-danger">{error}</small>}
        </Col>

        <Col xs={3} className="mt-2">
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !formValue.trim()}
          >
            {editingId ? "Update Benefit" : "Add Product Benefit"}
          </Button>
        </Col>
      </Row>

      <ReusableMuiTable
        title="Product Type Benefits"
        data={benefits}
        columns={columns}
        loading={loading}
        options={{
          search: true,
          serverSide: true,
          page: page - 1,
          count: totalCount,
          rowsPerPage: 10,
          selectableRows: "none",

          onChangePage: (p) => setPage(p + 1),

          onSearchChange: (searchText) => {
            setSearchBenifits(searchText || "");
            setPage(1);
          },

          onSearchClose: () => {
            setSearchBenifits("");
            setPage(1);
          },
        }}
      />

    </div>
  );
};

export default ProductTypeBenefits;
